import React, { useState, useCallback, useRef, useEffect } from 'react';
import { replaceAssignee, removeAssignee, swapOwner, batchUpdateAssignees } from '../../../api/ticketAssignees';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import UserSelect from '../../common/UserSelect';
import Avatar from '../../common/Avatar';

const TicketAssignees = ({ ticketId, assignees = [], canEdit, onUpdate, onActivityPush }) => {
  const { profile } = useAuth();
  const { companyUsers, usersMapByEmail } = useBetaData();
  const [selectMode, setSelectMode] = useState(null); // null | 'replace_owner' | 'add'
  const [optimisticIds, setOptimisticIds] = useState([]);
  const wrapperRef = useRef(null);

  // 從 prop 同步 optimisticIds
  useEffect(() => {
    setOptimisticIds(assignees.map((a) => a.user_id));
  }, [assignees]);

  // 點擊外部時自動收合選擇器
  useEffect(() => {
    if (!selectMode) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setSelectMode(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectMode]);

  const resolveUser = (assignee) => {
    const email = assignee?.profiles?.email?.toLowerCase();
    return email ? usersMapByEmail[email] : null;
  };

  const ownerId = optimisticIds[0];
  const collaboratorIds = optimisticIds.slice(1);

  // ─── Owner 替換 ───
  const handleReplaceOwner = useCallback((newUserId) => {
    if (!ownerId || newUserId === ownerId) return;

    const isAlreadyAssigned = optimisticIds.includes(newUserId);
    const oldUser = companyUsers.find((u) => u.id === ownerId);
    const newUser = companyUsers.find((u) => u.id === newUserId);

    if (isAlreadyAssigned) {
      // 交換：newUser 變 Owner，oldOwner 變一般成員
      setOptimisticIds((prev) => [newUserId, ...prev.filter((id) => id !== newUserId)]);
      setSelectMode(null);

      const runApi = async () => {
        const { error } = await swapOwner(
          ticketId, ownerId, newUserId, profile.id, oldUser || null, newUser || null
        );
        if (!error) {
          onUpdate();
          onActivityPush?.({
            id: `optimistic-assign-${Date.now()}`,
            action: 'assignment_change',
            user_name: profile?.name || profile?.email,
            metadata: {
              added: [{ user_id: newUserId, user_name: newUser?.name || newUser?.email }],
              removed: [{ user_id: ownerId, user_name: oldUser?.name || oldUser?.email }],
              type: 'owner_swap',
            },
            created_at: new Date().toISOString(),
          });
        }
      };
      runApi();
    } else {
      // 替換：移除舊 Owner，新增新 Owner
      setOptimisticIds((prev) => [newUserId, ...prev.slice(1)]);
      setSelectMode(null);

      const runApi = async () => {
        const { error } = await replaceAssignee(
          ticketId, ownerId, newUserId, profile.id, oldUser || null, newUser || null
        );
        if (!error) {
          onUpdate();
          onActivityPush?.({
            id: `optimistic-assign-${Date.now()}`,
            action: 'assignment_change',
            user_name: profile?.name || profile?.email,
            metadata: {
              added: [{ user_id: newUserId, user_name: newUser?.name || newUser?.email }],
              removed: [{ user_id: ownerId, user_name: oldUser?.name || oldUser?.email }],
            },
            created_at: new Date().toISOString(),
          });
        }
      };
      runApi();
    }
  }, [ticketId, ownerId, optimisticIds, companyUsers, profile?.id, onUpdate, onActivityPush]);

  const handleReplaceChange = useCallback((ids) => {
    if (ids.length === 0) return;
    handleReplaceOwner(ids[0]);
  }, [handleReplaceOwner]);

  // ─── 協作者：單人移除 ───
  const handleRemoveCollaborator = useCallback((userId) => {
    const user = companyUsers.find((u) => u.id === userId);
    const userName = user?.name || user?.email || userId;

    // Optimistic UI
    setOptimisticIds((prev) => prev.filter((id) => id !== userId));

    const runApi = async () => {
      const { error } = await removeAssignee(ticketId, userId, userName);
      if (!error) {
        onUpdate();
        onActivityPush?.({
          id: `optimistic-assign-${Date.now()}`,
          action: 'assignment_change',
          user_name: profile?.name || profile?.email,
          metadata: {
            added: [],
            removed: [{ user_id: userId, user_name: userName }],
          },
          created_at: new Date().toISOString(),
        });
      }
    };
    runApi();
  }, [ticketId, companyUsers, profile?.id, profile?.name, profile?.email, onUpdate, onActivityPush]);

  // ─── 協作者：新增（add 模式關閉時 batch commit） ───
  const addSnapshotRef = useRef(null);
  const [pendingAddIds, setPendingAddIds] = useState(null);

  useEffect(() => {
    if (selectMode === 'add') {
      addSnapshotRef.current = [];
      setPendingAddIds([]);
    }
  }, [selectMode]);

  const prevSelectModeRef = useRef(null);
  useEffect(() => {
    if (prevSelectModeRef.current === 'add' && selectMode !== 'add') {
      commitAddChanges();
    }
    prevSelectModeRef.current = selectMode;
  }, [selectMode]);

  const commitAddChanges = () => {
    const pending = pendingAddIds;
    if (!pending || pending.length === 0) return;

    // Optimistic UI
    setOptimisticIds((prev) => [...prev, ...pending]);

    const addedUsers = pending.map((id) => companyUsers.find((u) => u.id === id)).filter(Boolean);

    const runApi = async () => {
      const { error } = await batchUpdateAssignees(
        ticketId, pending, [], profile.id, addedUsers, []
      );
      if (!error) {
        onUpdate();
        onActivityPush?.({
          id: `optimistic-assign-${Date.now()}`,
          action: 'assignment_change',
          user_name: profile?.name || profile?.email,
          metadata: {
            added: pending.map((id) => {
              const u = companyUsers.find((cu) => cu.id === id);
              return { user_id: id, user_name: u?.name || u?.email || id };
            }),
            removed: [],
          },
          created_at: new Date().toISOString(),
        });
      }
    };
    runApi();

    addSnapshotRef.current = null;
    setPendingAddIds(null);
  };

  const handleAddChange = useCallback((newIds) => {
    setPendingAddIds(newIds);
  }, []);

  // 用 optimisticIds 來顯示
  const displayAssignees = optimisticIds.map((id) => {
    const fromProp = assignees.find((a) => a.user_id === id);
    if (fromProp) return fromProp;
    const user = companyUsers.find((u) => u.id === id);
    return { user_id: id, profiles: { email: user?.email } };
  });

  const ownerAssignee = displayAssignees[0];
  const collaborators = displayAssignees.slice(1);

  const renderAssigneeRow = (a, isOwner) => {
    const u = resolveUser(a) || companyUsers.find((cu) => cu.id === a.user_id);
    const name = u?.name || a.profiles?.email || '未知';
    return (
      <div
        key={a.user_id}
        className={`assignee-row ${canEdit && isOwner ? 'replaceable' : ''}`}
        onClick={canEdit && isOwner ? () => setSelectMode(selectMode === 'replace_owner' ? null : 'replace_owner') : undefined}
      >
        <Avatar avatarUrl={u?.avatar_url} name={name} size="xsmall" />
        <span className="assignee-name">{name}</span>
        {canEdit && isOwner && (
          <svg className="assignee-action-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {canEdit && !isOwner && (
          <button
            className="assignee-remove-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveCollaborator(a.user_id);
            }}
            title="移除"
          >
            &times;
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="ticket-assignees-sidebar" ref={wrapperRef}>
      {/* ─── 負責人 ─── */}
      <div className="assignee-sub-label">Owner</div>
      {ownerAssignee ? (
        <>
          {renderAssigneeRow(ownerAssignee, true)}
          {selectMode === 'replace_owner' && (
            <div className="assignee-select-area">
              <UserSelect
                values={[]}
                onChange={handleReplaceChange}
                users={companyUsers}
                excludeIds={[ownerId]}
                placeholder="搜尋人員..."
                defaultOpen
                singleSelect
              />
            </div>
          )}
        </>
      ) : (
        !canEdit && <span className="no-assignees">未指派</span>
      )}

      {/* ─── 協作者 ─── */}
      <div className="assignee-sub-label" style={{ marginTop: 12 }}>協作者</div>
      {collaborators.length > 0 && (
        <div className="assignee-list">
          {collaborators.map((a) => renderAssigneeRow(a, false))}
        </div>
      )}

      {canEdit && !selectMode && (
        <button className="assignee-add-btn" onClick={() => setSelectMode('add')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M7 2v10M2 7h10" strokeLinecap="round" />
          </svg>
          新增協作者
        </button>
      )}

      {selectMode === 'add' && (
        <div className="assignee-select-area">
          <UserSelect
            values={pendingAddIds || []}
            onChange={handleAddChange}
            users={companyUsers}
            excludeIds={optimisticIds}
            placeholder="搜尋人員..."
            defaultOpen
          />
        </div>
      )}

      {collaborators.length === 0 && !canEdit && (
        <span className="no-assignees">無</span>
      )}
    </div>
  );
};

export default TicketAssignees;
